import { useEffect } from 'react';

interface Options {
  onCompose?: () => void;
  onFocusSearch?: () => void;
  onHelp?: () => void;
  enabled?: boolean;
}

// Internal state so J/K navigation can advance through post cards
// across the whole page. We find cards by the `[data-post-card]`
// attribute which individual post rows opt into. First activation
// focuses the nearest to the top of the viewport.
function getPostCards(): HTMLElement[] {
  return Array.from(document.querySelectorAll<HTMLElement>('[data-post-card]'));
}

function currentIndex(cards: HTMLElement[]): number {
  if (cards.length === 0) return -1;
  const mid = window.scrollY + window.innerHeight / 3;
  let best = 0;
  let bestDist = Infinity;
  cards.forEach((el, i) => {
    const top = el.getBoundingClientRect().top + window.scrollY;
    const dist = Math.abs(top - mid);
    if (dist < bestDist) { bestDist = dist; best = i; }
  });
  return best;
}

function scrollToCard(el: HTMLElement) {
  const rect = el.getBoundingClientRect();
  const target = window.scrollY + rect.top - 100;
  window.scrollTo({ top: target, behavior: 'smooth' });
  // Visual feedback — flash the border for ~600ms
  el.style.transition = 'box-shadow 0.3s ease';
  // Nokia: pixel outline using currentColor (theme-aware), no purple.
  el.style.boxShadow = '0 0 0 2px currentColor';
  setTimeout(() => { el.style.boxShadow = ''; }, 600);
}

export function useKeyboardShortcuts({ onCompose, onFocusSearch, onHelp, enabled = true }: Options) {
  useEffect(() => {
    if (!enabled) return;

    const handler = (e: KeyboardEvent) => {
      // Skip if typing in an input/textarea/contenteditable
      const tag = (e.target as HTMLElement)?.tagName?.toLowerCase();
      if (
        tag === 'input' ||
        tag === 'textarea' ||
        (e.target as HTMLElement)?.isContentEditable
      ) return;

      // Skip if modifier keys are held (except for combos we want)
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      switch (e.key) {
        case 'n':
        case 'c':
          e.preventDefault();
          onCompose?.();
          break;
        case '/':
          e.preventDefault();
          onFocusSearch?.();
          break;
        case '?':
          e.preventDefault();
          onHelp?.();
          break;
        case 'j': {
          e.preventDefault();
          const cards = getPostCards();
          if (cards.length === 0) return;
          const idx = currentIndex(cards);
          const next = cards[Math.min(cards.length - 1, idx + 1)];
          if (next) scrollToCard(next);
          break;
        }
        case 'k': {
          e.preventDefault();
          const cards = getPostCards();
          if (cards.length === 0) return;
          const idx = currentIndex(cards);
          const prev = cards[Math.max(0, idx - 1)];
          if (prev) scrollToCard(prev);
          break;
        }
        case 'g': {
          // `gg` = top of feed. Track first `g` in a ref via data attr
          // on document.
          e.preventDefault();
          const doc = document.documentElement;
          if (doc.dataset.wipeGPending === '1') {
            doc.dataset.wipeGPending = '0';
            window.scrollTo({ top: 0, behavior: 'smooth' });
          } else {
            doc.dataset.wipeGPending = '1';
            setTimeout(() => { doc.dataset.wipeGPending = '0'; }, 500);
          }
          break;
        }
        case 'G':
          e.preventDefault();
          window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
          break;
      }
    };

    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [enabled, onCompose, onFocusSearch, onHelp]);
}
